import {assertTokenValidForMutation, handleResponse} from "@/services/service-helpers.ts";

const BASE_URL = `${import.meta.env.VITE_NBH_URL_ROOT}/api/special-assignments`

export type SpecialAssignmentMemberItem = {
    id: string
    memberNo: number
    firstName: string
    lastName: string
    serviceIds: string[]
}

export type SpecialAssignmentService = {
    id: string
    name: string
    description?: string
    pointsPerUnit: number
    isFixedPoints: boolean
}

export type CreateSpecialAssignmentBookingRequest = {
    serviceId: string
    memberIds: string[]
    activityDate: string
    startTime: string
    endTime: string
}

export type CreateSpecialAssignmentBookingResponse = {
    bookingId: string
    pointsPerMember: number
    memberCount: number
}

export type UpdateSpecialAssignmentBookingRequest = {
    serviceId: string
    activityDate: string
    startTime: string
    endTime: string
    memberIdsToRemove: string[]
}

export type SpecialAssignmentBookingHistoryItem = {
    bookingId: string
    activityDate: string
    startTime: string | null
    endTime: string | null
    serviceName: string
    memberCount: number
    pointsPerMember: number
    createdByUsername: string
    createdAt: string
    deletedAt: string | null
    deletedByUsername: string | null
    updatedAt: string | null
}

export type SpecialAssignmentBookingDetail = {
    bookingId: string
    serviceId: string
    serviceName: string
    isFixedPoints: boolean
    activityDate: string
    startTime: string | null
    endTime: string | null
    pointsPerMember: number
    memberCount: number
    isDeleted: boolean
}

export type SpecialAssignmentBookingAuditLog = {
    actionType: string
    description: string
    createdAt: string
    createdByUsername: string
}

export type BookingMemberItem = {
    memberId: string
    memberNo: number
    firstName: string
    lastName: string
}

export type PagedResponse<T> = {
    content: T[]
    totalElements: number
    totalPages: number
    number: number
    size: number
}

export async function getSpecialAssignmentServices(token: string | null): Promise<SpecialAssignmentService[]> {
    if (!token) return Promise.reject("No token provided")

    const response = await fetch(`${BASE_URL}/services`, {
        headers: new Headers({'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'})
    })

    return handleResponse<SpecialAssignmentService[]>(response) as Promise<SpecialAssignmentService[]>
}

export async function getMembersForSpecialAssignment(
    token: string | null,
    signal?: AbortSignal,
): Promise<SpecialAssignmentMemberItem[]> {
    if (!token) return Promise.reject("No token provided")

    const response = await fetch(`${BASE_URL}/members`, {
        headers: new Headers({'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}),
        signal,
    })

    return handleResponse<SpecialAssignmentMemberItem[]>(response) as Promise<SpecialAssignmentMemberItem[]>
}

export async function getSpecialAssignmentBookings(
    token: string | null,
    page: number = 0,
    size: number = 20,
): Promise<PagedResponse<SpecialAssignmentBookingHistoryItem>> {
    if (!token) return Promise.reject("No token provided")

    const response = await fetch(`${BASE_URL}/bookings?page=${page}&size=${size}`, {
        headers: new Headers({'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'})
    })

    return handleResponse<PagedResponse<SpecialAssignmentBookingHistoryItem>>(response) as Promise<PagedResponse<SpecialAssignmentBookingHistoryItem>>
}

export async function getBookingDetail(
    token: string | null,
    bookingId: string,
): Promise<SpecialAssignmentBookingDetail> {
    if (!token) return Promise.reject("No token provided")

    const response = await fetch(`${BASE_URL}/bookings/${bookingId}`, {
        headers: new Headers({'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'})
    })

    return handleResponse<SpecialAssignmentBookingDetail>(response) as Promise<SpecialAssignmentBookingDetail>
}

export async function getBookingMembers(
    token: string | null,
    bookingId: string,
): Promise<BookingMemberItem[]> {
    if (!token) return Promise.reject("No token provided")

    const response = await fetch(`${BASE_URL}/bookings/${bookingId}/members`, {
        headers: new Headers({'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'})
    })

    return handleResponse<BookingMemberItem[]>(response) as Promise<BookingMemberItem[]>
}

export async function getBookingAuditLogs(
    token: string | null,
    bookingId: string,
): Promise<SpecialAssignmentBookingAuditLog[]> {
    if (!token) return Promise.reject("No token provided")

    const response = await fetch(`${BASE_URL}/bookings/${bookingId}/audit-logs`, {
        headers: new Headers({'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'})
    })

    return handleResponse<SpecialAssignmentBookingAuditLog[]>(response) as Promise<SpecialAssignmentBookingAuditLog[]>
}

export async function updateSpecialAssignmentBooking(
    token: string | null,
    bookingId: string,
    request: UpdateSpecialAssignmentBookingRequest,
): Promise<void> {
    if (!token) return Promise.reject("No token provided")
    assertTokenValidForMutation()

    const response = await fetch(`${BASE_URL}/bookings/${bookingId}`, {
        method: 'PUT',
        headers: new Headers({'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}),
        body: JSON.stringify(request),
    })

    await handleResponse(response)
}

export async function deleteSpecialAssignmentBooking(
    token: string | null,
    bookingId: string,
): Promise<void> {
    if (!token) return Promise.reject("No token provided")
    assertTokenValidForMutation()

    const response = await fetch(`${BASE_URL}/bookings/${bookingId}`, {
        method: 'DELETE',
        headers: new Headers({'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}),
    })

    await handleResponse(response)
}

export async function createSpecialAssignmentBooking(
    token: string | null,
    request: CreateSpecialAssignmentBookingRequest,
): Promise<CreateSpecialAssignmentBookingResponse> {
    if (!token) return Promise.reject("No token provided")
    assertTokenValidForMutation()

    const response = await fetch(`${BASE_URL}/bookings`, {
        method: 'POST',
        headers: new Headers({'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json'}),
        body: JSON.stringify(request),
    })

    return handleResponse<CreateSpecialAssignmentBookingResponse>(response) as Promise<CreateSpecialAssignmentBookingResponse>
}
